import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Autocomplete,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "../../contexts/snackbar-context";
import ConfirmDialog from "../../components/common/confirm-dialog";
import { useOnBoarding } from "../../hooks/useOnBoarding";
import { Tenant, User, TenantFeature } from "../../services/onBoardingService";

// Common style constants
const COLORS = {
  white: "#fff",
  primary: "hsl(19 101% 51% / 1)",
  primaryHover: "hsl(19 101% 41% / 1)",
};

const commonInputStyles = {
  "& .MuiInputLabel-root": {
    color: `${COLORS.white} !important`,
  },
  "& .MuiOutlinedInput-root": {
    color: COLORS.white,
    "& fieldset": {
      borderColor: `${COLORS.white} !important`,
    },
    "&:hover fieldset": {
      borderColor: `${COLORS.primary} !important`,
    },
    "&.Mui-focused fieldset": {
      borderColor: `${COLORS.primary} !important`,
    },
  },
  "& .MuiAutocomplete-popupIndicator": {
    color: `${COLORS.white} !important`,
  },
  "& .MuiAutocomplete-clearIndicator": {
    color: `${COLORS.white} !important`,
  },
};

const TenantFeatureMapping: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const { 
    tenants, 
    getTenantUsers, 
    getAvailableFeatures, 
    updateTenantFeatures,
    loadTenants 
  } = useOnBoarding();
  
  // API-driven state
  const [availableFeatures, setAvailableFeatures] = useState<TenantFeature[]>([]);
  const [mockUsers, setMockUsers] = useState<User[]>([]);

  // State
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [enabledFeatures, setEnabledFeatures] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Load tenants on mount
  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // Load available features on mount
  useEffect(() => {
    const loadFeatures = async () => {
      const features = await getAvailableFeatures();
      setAvailableFeatures(features);
    };
    loadFeatures();
  }, [getAvailableFeatures]);

  // Load tenant users when tenant changes
  useEffect(() => {
    if (selectedTenant) {
      const loadUsers = async () => {
        const users = await getTenantUsers(selectedTenant.id);
        setMockUsers(users);
      };
      loadUsers();
    }
  }, [selectedTenant, getTenantUsers]);

  // Initialize enabled features when tenant changes
  useEffect(() => {
    if (selectedTenant) {
      setEnabledFeatures(new Set(selectedTenant.features));
      setIsEditMode(false);
      setHasChanges(false);
      setSelectedUsers([]);
    }
  }, [selectedTenant]);

  // Check for changes in summary (features or users)
  const checkForChanges = (newFeatures: Set<string>, newUsers: User[]) => {
    const originalFeatures = new Set(selectedTenant?.features || []);
    const featuresChanged = 
      newFeatures.size !== originalFeatures.size ||
      Array.from(newFeatures).some(f => !originalFeatures.has(f));
    
    const usersChanged = newUsers.length > 0;
    
    setHasChanges(featuresChanged || usersChanged);
  };

  // Get users for selected tenant
  const tenantUsers = selectedTenant 
    ? mockUsers.filter(u => u.tenantId === selectedTenant.id)
    : [];

  const handleTenantChange = (tenant: Tenant | null) => {
    setSelectedTenant(tenant);
  };

  const handleUserChange = (user: User | null) => {
    if (!user || selectedUsers.find(u => u.id === user.id)) return;
    const newUsers = [...selectedUsers, user];
    setSelectedUsers(newUsers);
    checkForChanges(enabledFeatures, newUsers);
  };

  const handleRemoveUser = (userId: string) => {
    const newUsers = selectedUsers.filter(u => u.id !== userId);
    setSelectedUsers(newUsers);
    checkForChanges(enabledFeatures, newUsers);
  };

  const handleEnableEdit = () => {
    setIsEditMode(true);
  };

  const handleFeatureToggle = (featureId: string) => {
    if (!isEditMode) return;
    
    const newEnabled = new Set(enabledFeatures);
    if (newEnabled.has(featureId)) {
      newEnabled.delete(featureId);
    } else {
      newEnabled.add(featureId);
    }
    setEnabledFeatures(newEnabled);
    checkForChanges(newEnabled, selectedUsers);
  };

  const handleSave = async () => {
    setShowConfirmDialog(false);
    setIsLoading(true);
    
    if (!selectedTenant) {
      setIsLoading(false);
      return;
    }

    try {
      await updateTenantFeatures(
        selectedTenant.id,
        Array.from(enabledFeatures)
      );
      
      setHasChanges(false);
      setIsEditMode(false);
      showSuccess("Features updated successfully!");
        
      // Update local tenant data
      selectedTenant.features = Array.from(enabledFeatures);
    } catch (error) {
      showError("Failed to update feature mapping");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedTenant(null);
    setSelectedUsers([]);
    setEnabledFeatures(new Set());
    setIsEditMode(false);
    setHasChanges(false);
  };

  const isPageDisabled = !selectedTenant;

  return (
    <Box sx={{ height: "100%", p: 2 }}>
      <Paper
        sx={{
          p: 3,
          bgcolor: "#111",
          color: "#fff",
          border: "1px solid #333",
          height: "100%",
          overflow: "auto",
        }}
      >
        <Typography
          variant="h5"
          sx={{ color: COLORS.primary, mb: 3 }}
        >
          Tenant Feature Mapping
        </Typography>
        <Box sx={{ display: "flex", minHeight: 500 }}>
          {/* Left Panel: Dropdowns + Summary */}
          <Box sx={{ width: "30%", pr: 3, borderRight: "1px solid #222", position: "relative" }}>
            <Box sx={{ position: "sticky", top: 0, zIndex: 1, bgcolor: "#111", pb: 2 }}>
              {/* Tenant Selection */}
              <Autocomplete
                options={tenants}
                value={selectedTenant}
                onChange={(_, value) => handleTenantChange(value)}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Tenant *"
                    fullWidth
                    required
                    sx={commonInputStyles}
                  />
                )}
                sx={{ mb: 2 }}
              />
              {/* Multi-User Selection (Preview Only) */}
              <Typography sx={{ color: "#fff", fontSize: "0.9rem", mb: 1, fontWeight: 500 }}>
                Add Admin to this tenant
              </Typography>
              <Autocomplete
                options={tenantUsers.filter(u => !selectedUsers.find(su => su.id === u.id))}
                value={null}
                onChange={(_, value) => handleUserChange(value)}
                getOptionLabel={(option) => option.email}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={isPageDisabled}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    sx={commonInputStyles}
                    placeholder="Search by email..."
                  />
                )}
                sx={{ mb: 2 }}
              />
              <Divider sx={{ borderColor: "#333", mb: 2 }} />
              {/* Summary Section */}
              <Typography variant="h6" sx={{ color: "#fff", mb: 2 }}>
                Summary
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                <Box>
                  <Typography sx={{ color: "#aaa", fontSize: "0.9rem" }}>Selected Tenant:</Typography>
                  <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                    {selectedTenant?.name || "None selected"}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ color: "#aaa", fontSize: "0.9rem" }}>Enabled Features:</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                    {Array.from(enabledFeatures).map(featureId => {
                      const feature = availableFeatures.find(f => f.id === featureId);
                      return (
                        <Chip
                          key={featureId}
                          label={feature?.name}
                          size="small"
                          sx={{
                            backgroundColor: COLORS.primary,
                            color: "#fff",
                          }}
                        />
                      );
                    })}
                    {enabledFeatures.size === 0 && (
                      <Typography sx={{ color: "#666", fontStyle: "italic" }}>
                        No features enabled
                      </Typography>
                    )}
                  </Box>
                </Box>
                {/* Selected Users in Summary */}
                {selectedUsers.length > 0 && (
                  <Box>
                    <Typography sx={{ color: "#aaa", fontSize: "0.9rem" }}>
                      Selected Users (Preview): {selectedUsers.length}
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                      {selectedUsers.map((user) => (
                        <Chip
                          key={user.id}
                          label={`${user.email} (${user.role})`}
                          onDelete={() => handleRemoveUser(user.id)}
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.1)",
                            color: "#fff",
                            "& .MuiChip-deleteIcon": {
                              color: "#fff",
                              "&:hover": {
                                color: COLORS.primary,
                              },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
              {/* Save/Cancel Buttons - Always Visible (now in left panel) */}
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isLoading || isPageDisabled}
                  sx={{
                    color: "#fff",
                    borderColor: "#fff",
                    "&:hover": {
                      borderColor: "#fff",
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setShowConfirmDialog(true)}
                  disabled={!hasChanges || isLoading || isPageDisabled}
                  sx={{
                    backgroundColor: COLORS.primary,
                    "&:hover": { backgroundColor: COLORS.primaryHover },
                    "&:disabled": {
                      backgroundColor: "hsl(19 101% 51% / 0.4)",
                      color: "#fff",
                    },
                  }}
                >
                  {isLoading ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            </Box>
          </Box>
          {/* Right Panel: Feature Configuration */}
          <Box sx={{ width: "70%", pl: 3 }}>
            <Box sx={{ opacity: isPageDisabled ? 0.5 : 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ color: "#fff" }}>
                  Feature Configuration
                  {!isEditMode && (
                    <Chip
                      label="View Mode"
                      size="small"
                      sx={{ ml: 2, backgroundColor: "#666", color: "#fff" }}
                    />
                  )}
                  {isEditMode && (
                    <Chip
                      label="Edit Mode"
                      size="small"
                      sx={{ ml: 2, backgroundColor: COLORS.primary, color: "#fff" }}
                    />
                  )}
                </Typography>
                {/* Enable Edit / Exit Edit Mode Toggle */}
                {isEditMode ? (
                  <Button
                    variant="outlined"
                    onClick={() => setIsEditMode(false)}
                    disabled={isLoading || isPageDisabled}
                    sx={{
                      color: "#fff",
                      borderColor: "#fff",
                      "&:hover": {
                        borderColor: "#fff",
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    Exit Edit Mode
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEnableEdit}
                    disabled={isPageDisabled}
                    sx={{
                      backgroundColor: COLORS.primary,
                      "&:hover": { backgroundColor: COLORS.primaryHover },
                    }}
                  >
                    Enable Edit
                  </Button>
                )}
              </Box>
              {/* Features List */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 3 }}>
                {/* Group features by category */}
                {['Assets', 'Documents', 'Admin'].map((category) => {
                  const categoryFeatures = availableFeatures.filter(f => f.category === category);
                  return (
                    <Box key={category}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: COLORS.primary, 
                          mb: 2, 
                          fontWeight: 600,
                          borderBottom: '1px solid #333',
                          pb: 1
                        }}
                      >
                        {category}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pl: 2 }}>
                        {categoryFeatures.map((feature) => (
                          <Box key={feature.id}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={enabledFeatures.has(feature.id)}
                                  onChange={() => handleFeatureToggle(feature.id)}
                                  disabled={!isEditMode || isPageDisabled}
                                  sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                      color: COLORS.primary,
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                      backgroundColor: COLORS.primary,
                                    },
                                    "& .MuiSwitch-track": {
                                      backgroundColor: "#666",
                                    },
                                  }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography sx={{ color: "#fff", fontWeight: 500 }}>
                                    {feature.name}
                                  </Typography>
                                  <Typography sx={{ color: "#aaa", fontSize: "0.85rem" }}>
                                    {feature.description}
                                  </Typography>
                                </Box>
                              }
                              sx={{ alignItems: "flex-start", width: "100%", ml: 0 }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={showConfirmDialog}
        title="Confirm Feature Mapping Changes"
        message={`These changes will affect all users in ${selectedTenant?.name}. Are you sure you want to continue?`}
        confirmLabel="Save Changes"
        onConfirm={handleSave}
        onCancel={() => setShowConfirmDialog(false)}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default TenantFeatureMapping;